import { BookOpen, CalendarDays, GraduationCap, Plus, Star, Target } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { DegerlendirmeModal } from '../components/actions/DegerlendirmeModal'
import { EgitimTimelineCard } from '../components/EgitimTimelineCard'
import { KpiCard } from '../components/KpiCard'
import { ManagerNoteCard } from '../components/ManagerNoteCard'
import { MobileList } from '../components/MobileList'
import { TABLE_FRAME } from '../components/tableFrame'
import { SectionCard } from '../components/SectionCard'
import type { PersonelDetail } from '../model'
import { exportIcs } from '../lib/exportActions'
import { dmyToIso } from '../lib/fileExport'
import type { Personel } from '../types'
import { HR_WRITE_PERM } from '../../permissions'

function HedefPill({ durum }: { durum: 'yolda' | 'risk' | 'tamam' }) {
  const { t } = useTranslation()
  const cls =
    durum === 'tamam' ? 'bg-success/12 text-success' : durum === 'risk' ? 'bg-warning/15 text-foreground' : 'bg-info/12 text-info'
  return (
    <span className={cn('inline-flex rounded-full px-2.5 py-1 text-xs font-medium', cls)}>
      {t(`hr.personel.perf.durum.${durum}`)}
    </span>
  )
}

export function PerformansTab({ personel, perf }: { personel: Personel; perf: PersonelDetail['perf'] }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const { kpi } = perf
  const [degOpen, setDegOpen] = useState(false)
  const ustunde = kpi.skorDeger >= 4
  const takvim = () =>
    exportIcs(`degerlendirme_${personel.sicil}.ics`, {
      title: t('hr.personel.actions.degerlendirme.ics', { name: personel.ad }),
      date: dmyToIso(kpi.sonraki),
      description: `${personel.pozisyon} · ${personel.departman}`,
    })

  return (
    <div className="space-y-5">
      {degOpen ? <DegerlendirmeModal personelId={personel.id} onClose={() => setDegOpen(false)} /> : null}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4">
        <KpiCard
          label={t('hr.personel.perf.skor')}
          value={kpi.skor}
          hint={t('hr.personel.perf.skorTarih', { date: kpi.skor_tarih })}
          tint="warning"
          icon={<Star className="size-5" />}
        />
        <KpiCard
          label={t('hr.personel.perf.hedefTamam')}
          value={kpi.hedefler}
          hint={t('hr.personel.perf.buYil', { year: kpi.hedef_yil })}
          tint="success"
          icon={<Target className="size-5" />}
        />
        <KpiCard
          label={t('hr.personel.perf.egitimTamam')}
          value={kpi.egitim_tamam}
          hint={t('hr.personel.perf.sertifikali')}
          tint="info"
          icon={<GraduationCap className="size-5" />}
        />
        <KpiCard
          label={t('hr.personel.perf.egitimPlan')}
          value={kpi.egitim_plan}
          hint={t('hr.personel.perf.yaklasanEgitim')}
          tint="accent"
          icon={<BookOpen className="size-5" />}
        />
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)] gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-5">
          <SectionCard
            title={t('hr.personel.perf.hedefList')}
            action={<span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">{kpi.hedef_yil}</span>}
            noPad
          >
            <MobileList
              rows={perf.hedefler.map((h) => ({
                key: h.id,
                title: h.ad,
                subtitle: h.aciklama,
                trailing: <HedefPill durum={h.durum} />,
                meta: (
                  <>
                    <div className="flex w-full items-center gap-2.5">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                        <div className="h-full rounded-full bg-primary" style={{ width: `${h.ilerleme}%` }} />
                      </div>
                      <span className="font-medium tabular-nums">%{h.ilerleme}</span>
                    </div>
                    <span>
                      {t('hr.personel.perf.sonTarih')}: <span className="tabular-nums text-foreground">{h.son_tarih}</span>
                    </span>
                  </>
                ),
              }))}
            />
            <Table className="hr-table" wrapperClassName={TABLE_FRAME}>
              <THead>
                <TR className="hover:bg-transparent">
                  <TH>{t('hr.personel.perf.hedef')}</TH>
                  <TH className="w-[180px]">{t('hr.personel.perf.ilerleme')}</TH>
                  <TH>{t('hr.personel.perf.sonTarih')}</TH>
                  <TH>{t('hr.personel.columns.durum')}</TH>
                </TR>
              </THead>
              <TBody>
                {perf.hedefler.map((h) => (
                  <TR key={h.id}>
                    <TD>
                      <div className="font-semibold">{h.ad}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">{h.aciklama}</div>
                    </TD>
                    <TD>
                      <div className="flex items-center gap-2.5">
                        <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                          <div className="h-full rounded-full bg-primary" style={{ width: `${h.ilerleme}%` }} />
                        </div>
                        <span className="w-9 text-right text-xs font-medium tabular-nums text-muted-foreground">%{h.ilerleme}</span>
                      </div>
                    </TD>
                    <TD className="whitespace-nowrap text-muted-foreground">{h.son_tarih}</TD>
                    <TD>
                      <HedefPill durum={h.durum} />
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </SectionCard>

          <SectionCard
            title={
              <div className="flex flex-wrap items-center gap-2">
                <span>{t('hr.personel.perf.degerlendirme')}</span>
                <span className="text-xs font-normal text-muted-foreground">{kpi.skor_tarih}</span>
              </div>
            }
            action={
              can(HR_WRITE_PERM) ? (
                <Button size="sm" onClick={() => setDegOpen(true)}>
                  <Plus />
                  {t('hr.personel.perf.degerlendirmeEkle')}
                </Button>
              ) : undefined
            }
          >
            <div className="grid grid-cols-[minmax(0,1fr)] gap-5 lg:grid-cols-[200px_minmax(0,1fr)_220px]">
              <div className="space-y-3">
                <div className="text-3xl font-semibold tracking-tight tabular-nums">{kpi.skor}</div>
                <div className="flex gap-0.5 text-warning" aria-hidden>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className={cn('size-4', i < Math.round(kpi.skorDeger) ? 'fill-current' : 'opacity-40')} />
                  ))}
                </div>
                <span
                  className={cn(
                    'inline-flex rounded-full px-2.5 py-1 text-xs font-medium',
                    ustunde ? 'bg-success/12 text-success' : 'bg-info/12 text-info',
                  )}
                >
                  {t(ustunde ? 'hr.personel.perf.ustunde' : 'hr.personel.perf.beklenen')}
                </span>
              </div>

              <ManagerNoteCard notlar={perf.notlar} />

              <div className="space-y-3">
                <div className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                  {t('hr.personel.perf.yetkinlik')}
                </div>
                {perf.yetkinlikler.map((y) => (
                  <div key={y.ad} className="space-y-1.5">
                    <div className="flex items-center justify-between gap-2 text-[13px]">
                      <span className="font-medium">{y.ad}</span>
                      <span className="tabular-nums text-muted-foreground">{y.skor}</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                      <div className="h-full rounded-full bg-primary" style={{ width: `${y.pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </SectionCard>
        </div>

        <div className="space-y-4">
          <EgitimTimelineCard personel={personel} egitim={perf.egitim} />

          <SectionCard>
            <div className="flex items-start gap-3">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/12 text-primary sm:size-10 sm:rounded-xl">
                <CalendarDays className="size-4 sm:size-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-muted-foreground">{t('hr.personel.perf.sonraki')}</div>
                <div className="mt-0.5 text-base font-semibold tabular-nums">{kpi.sonraki}</div>
                <Button variant="outline" size="sm" className="mt-3 w-full bg-card" onClick={takvim}>
                  {t('hr.personel.perf.takvimeEkle')}
                </Button>
              </div>
            </div>
          </SectionCard>
        </div>
      </div>
    </div>
  )
}
